import {
  Box,
  HStack,
  VStack,
  Button,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { DataTablePaginationProps } from '../types'
import { DropdownSelect } from '@/components/common/DropdownSelect'

export function DataTablePagination({
  pagination,
  onPageChange,
  onPageSizeChange,
  totalLabel = 'items',
}: DataTablePaginationProps) {
  const { t } = useTranslation()
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const textColor = useColorModeValue('gray.600', 'gray.300')

  const { page, pageSize, total } = pagination
  const totalPages = Math.ceil(total / pageSize)
  const startItem = (page - 1) * pageSize + 1
  const endItem = Math.min(page * pageSize, total)

  const pageSizeOptions = pagination.pageSizeOptions || [10, 20, 50, 100]
  const pageSizeDropdownOptions = pageSizeOptions.map(size => ({
    value: String(size),
    label: String(size),
  }))
  const pageSizeAriaLabel = t('common.pagination.pageSize')

  const getVisiblePages = () => {
    const delta = 2
    const range = []
    const rangeWithDots = []

    for (
      let i = Math.max(2, page - delta);
      i <= Math.min(totalPages - 1, page + delta);
      i++
    ) {
      range.push(i)
    }

    if (page - delta > 2) {
      rangeWithDots.push(1, '...')
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (page + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages)
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots
  }

  if (total === 0) {
    return null
  }

  return (
    <Box p={4} borderTop="1px" borderColor={borderColor}>
      <VStack spacing={4}>
        {/* Mobile layout */}
        <VStack spacing={3} display={{ base: 'flex', md: 'none' }} align="stretch">
          <Text fontSize="sm" color={textColor} textAlign="center">
            {t('common.pagination.showing', {
              from: startItem,
              to: endItem,
              total: total
            })} {totalLabel}
          </Text>
          
          <HStack justify="center" spacing={2}>
            <Button
              size="sm"
              onClick={() => onPageChange(page - 1)}
              isDisabled={page === 1}
            >
              {t('common.pagination.previous')}
            </Button>
            
            <Text fontSize="sm" px={2}>
              {page} / {totalPages}
            </Text>
            
            <Button
              size="sm"
              onClick={() => onPageChange(page + 1)}
              isDisabled={page >= totalPages}
            >
              {t('common.pagination.next')}
            </Button>
          </HStack>

          <HStack justify="center" spacing={2}>
            <Text fontSize="sm" color={textColor}>
              {t('common.pagination.pageSize')}:
            </Text>
            <Box w="84px">
              <DropdownSelect
                size="xs"
                value={String(pageSize)}
                options={pageSizeDropdownOptions}
                ariaLabel={pageSizeAriaLabel}
                onChange={value => onPageSizeChange(Number(value))}
              />
            </Box>
          </HStack>
        </VStack>

        {/* Desktop layout */}
        <HStack 
          justify="space-between" 
          align="center" 
          width="100%" 
          display={{ base: 'none', md: 'flex' }}
        >
          <HStack spacing={4}>
            <Text fontSize="sm" color={textColor}>
              {t('common.pagination.showing', {
                from: startItem,
                to: endItem,
                total: total
              })} {totalLabel}
            </Text>

            <HStack spacing={2}>
              <Text fontSize="sm" color={textColor}>
                {t('common.pagination.pageSize')}:
              </Text>
              <Box w="84px">
                <DropdownSelect
                  size="xs"
                  value={String(pageSize)}
                  options={pageSizeDropdownOptions}
                  ariaLabel={pageSizeAriaLabel}
                  onChange={value => onPageSizeChange(Number(value))}
                />
              </Box>
            </HStack>
          </HStack>

          <HStack spacing={1}>
            <Button
              size="sm"
              onClick={() => onPageChange(page - 1)}
              isDisabled={page === 1}
            >
              {t('common.pagination.previous')}
            </Button>

            {getVisiblePages().map((pageNum, index) => (
              <Button
                key={index}
                size="sm"
                variant={pageNum === page ? 'solid' : 'ghost'}
                colorScheme={pageNum === page ? 'admin' : 'gray'}
                onClick={() => typeof pageNum === 'number' && onPageChange(pageNum)}
                isDisabled={pageNum === '...'}
                minW="40px"
              >
                {pageNum}
              </Button>
            ))}

            <Button
              size="sm"
              onClick={() => onPageChange(page + 1)}
              isDisabled={page >= totalPages}
            >
              {t('common.pagination.next')}
            </Button>
          </HStack>
        </HStack>
      </VStack>
    </Box>
  )
}
